import { useBooleanState } from '@mfw/components';
import React from 'react';

import { useOrganizationContext, useAuthContext } from '../../context';
import useLogicalProfiles from '../../hooks/users/useLogicalProfiles';
import SwitchProfileModal from './SwitchProfileModal';

/**
 * Forces a profile choice at the start of every explicit sign-in.
 *
 * Gated on `sessionRevision` (`AuthProvider`), which advances ONLY on an explicit
 * sign-in boundary (password, Google, corp credential transition) — never on a
 * token rollover or a plain page reload of an already-open session. That is what
 * makes this "every time they log in", not "every time the app loads": a refresh
 * mid-session must not reopen a modal the user already dismissed.
 *
 * Only for accounts whose switcher actually offers more than one row. This is
 * deliberately narrower than `SettingsDropdown`'s "Switch profile" rule: there
 * the modal can be closed, so personal mode + one organization is a real
 * (optional) switch; here it would be a forced single-row pick. A platform user (P1/P6) is excluded outright: it holds no
 * membership, and the org-less curation workspace is already its only real
 * context, so forcing a pick here would just be an extra click with no choice
 * behind it.
 */
const ForcedSwitchProfileModal = () => {
  const { currentUser, sessionRevision } = useAuthContext();
  const { currentCorpOrganization } = useOrganizationContext();
  const forcedModal = useBooleanState();

  // The latest `forcedModal`, read inside the effect below through this ref
  // rather than the dependency array. `useBooleanState`'s memo is keyed on
  // `isActive`, so a dependency on the object — or even on `.on` alone — would
  // make the effect's identity of "did anything real change" track the
  // modal's own open/close state instead of the login boundary it exists to
  // gate on.
  const forcedModalRef = React.useRef(forcedModal);
  forcedModalRef.current = forcedModal;

  const isPlatformUser = Boolean(currentUser?.isPlatformUser);
  // Mirrors the onboarding/privacy gates the rest of the app already applies
  // before offering the switcher (see `SettingsDropdown`'s disabled entries and
  // `PrivacyPolicyModal`) — an account still finishing either of those has
  // nothing to switch into yet. ACTIVE too: `/verification` renders under the
  // same shell, and an account whose e-mail is not confirmed may not read its
  // profiles at all (the guard answers 403, which surfaced as an error toast
  // on the first screen of every signup — review 2026-10-01).
  const canPrompt =
    currentUser?.status === 'ACTIVE' &&
    Boolean(currentUser?.hasFinishedOnboarding) &&
    Boolean(currentUser?.settings?.privacyPolicyAccepted);
  // Only ask for the profiles once the modal could actually prompt.
  const logicalProfiles = useLogicalProfiles(!isPlatformUser && canPrompt);
  const orgMembershipCount = (logicalProfiles.data ?? []).filter(
    (profile) => profile.organizationId !== null,
  ).length;

  // Counts the rows `SwitchProfileModal` actually renders — the corp
  // organization and each membership. The personal profile is not a row there,
  // so being in personal mode adds no choice: with a single row the forced
  // (non-dismissable) modal would leave nothing to pick but that row.
  const offeredProfileCount =
    (currentCorpOrganization?.id ? 1 : 0) + orgMembershipCount;
  const hasChoice = !isPlatformUser && offeredProfileCount > 1;

  // Which `sessionRevision` this account has already resolved a pick for.
  // `undefined` on mount/reload — sessionRevision is 0 then too, so the effect
  // below never opens for it.
  const resolvedRevisionRef = React.useRef<number | undefined>(undefined);
  // Tracked separately from the ref above: only a genuine open → close
  // transition marks a revision resolved. Without this, the "mark resolved"
  // effect would also fire on every render where the modal simply HASN'T
  // opened yet (isActive starts `false`), stamping the revision as resolved
  // before `hasChoice`/`canPrompt` ever had a chance to turn true once their
  // data finished loading.
  const wasActiveRef = React.useRef(false);

  React.useEffect(() => {
    if (!sessionRevision || !hasChoice || !canPrompt) return;
    if (resolvedRevisionRef.current === sessionRevision) return;
    forcedModalRef.current.on();
  }, [sessionRevision, hasChoice, canPrompt]);

  React.useEffect(() => {
    if (wasActiveRef.current && !forcedModal.isActive) {
      resolvedRevisionRef.current = sessionRevision;
    }
    wasActiveRef.current = forcedModal.isActive;
  }, [forcedModal.isActive, sessionRevision]);

  if (!forcedModal.isActive) return null;
  return <SwitchProfileModal switchProfileModal={forcedModal} forced />;
};

export default ForcedSwitchProfileModal;
